/** Mutación pendiente de enviar. Su `id` es también la Idempotency-Key: reintentar nunca duplica. */
export interface Mutacion {
  id: string;
  ruta: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  descripcion?: string;
  /** Usuario que hizo el cambio: la cola solo lo envía con la sesión de esa misma persona. */
  propietario?: string;
  intentos: number;
  creadaEn: string;
  error?: string;
}

/** Almacenamiento clave-valor persistente (SQLite en la app, localStorage en la web, memoria en pruebas). */
export interface Almacen {
  leer(clave: string): Promise<string | null>;
  escribir(clave: string, valor: string): Promise<void>;
}

/** ok = aplicado; conflicto = el servidor lo rechazó (requiere revisión); reintentar = sin conexión. */
export type ResultadoEnvio = 'ok' | 'conflicto' | 'reintentar';

const CLAVE_PENDIENTES = 'cola:pendientes';
const CLAVE_CONFLICTOS = 'cola:conflictos';

/**
 * Cola FIFO de escrituras hechas sin conexión. Se procesa en orden al recuperar la red;
 * un rechazo del servidor no bloquea a las siguientes y queda para revisión manual.
 */
export class ColaOffline {
  private procesando: Promise<void> | null = null;

  constructor(
    private readonly almacen: Almacen,
    private readonly enviar: (m: Mutacion) => Promise<ResultadoEnvio>,
  ) {}

  private async leer(clave: string): Promise<Mutacion[]> {
    const texto = await this.almacen.leer(clave);
    return texto ? (JSON.parse(texto) as Mutacion[]) : [];
  }

  private guardar(clave: string, lista: Mutacion[]): Promise<void> {
    return this.almacen.escribir(clave, JSON.stringify(lista));
  }

  pendientes(): Promise<Mutacion[]> {
    return this.leer(CLAVE_PENDIENTES);
  }

  conflictos(): Promise<Mutacion[]> {
    return this.leer(CLAVE_CONFLICTOS);
  }

  async encolar(m: Omit<Mutacion, 'intentos' | 'creadaEn'>): Promise<void> {
    const lista = await this.pendientes();
    if (lista.some((x) => x.id === m.id)) return;
    await this.guardar(CLAVE_PENDIENTES, [...lista, { ...m, intentos: 0, creadaEn: new Date().toISOString() }]);
  }

  async descartarConflicto(id: string): Promise<void> {
    await this.guardar(CLAVE_CONFLICTOS, (await this.conflictos()).filter((m) => m.id !== id));
  }

  /** Envía las pendientes en orden. Evita dos procesos simultáneos. */
  procesar(): Promise<void> {
    this.procesando ??= this.procesarInterno().finally(() => {
      this.procesando = null;
    });
    return this.procesando;
  }

  private async procesarInterno(): Promise<void> {
    for (;;) {
      const [primera, ...resto] = await this.pendientes();
      if (!primera) return;
      let resultado: ResultadoEnvio;
      try {
        resultado = await this.enviar(primera);
      } catch {
        resultado = 'reintentar';
      }
      if (resultado === 'reintentar') {
        await this.guardar(CLAVE_PENDIENTES, [{ ...primera, intentos: primera.intentos + 1 }, ...resto]);
        return;
      }
      if (resultado === 'conflicto') await this.guardar(CLAVE_CONFLICTOS, [...(await this.conflictos()), primera]);
      await this.guardar(CLAVE_PENDIENTES, resto);
    }
  }
}
